import { Skeleton } from '@folio/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@folio/components/ui/tooltip';
import { cn } from '@folio/lib';
import { Button } from '../../ui/button';
import { FlagIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import type { GetDocumentExtractionOutput, SubmitFeedbackBody } from 'korint-sdk';
import { ExtractionFeedbackForm } from './ExtractionFeedbackForm';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export interface ReportedFieldInfo {
  correctedValue?: string;
  comment?: string;
}

interface ExtractionFieldsDisplayProps {
  extraction: GetDocumentExtractionOutput | null | undefined;
  isLoading?: boolean;
  extractionEnabled?: boolean;
  onFeedback?: (feedback: SubmitFeedbackBody) => void | Promise<void>;
  isFeedbackPending?: boolean;
  reportedFields?: Map<string, ReportedFieldInfo>;
  isDocumentReported?: boolean;
}

const CLAIM_FIELD_PATTERN = /^claim(\d+)(Date|Nature|Responsibility)$/;

interface ClaimRow {
  number: number;
  date: { key: string; value: unknown } | null;
  nature: { key: string; value: unknown } | null;
  responsibility: { key: string; value: unknown } | null;
}

function groupFields(entries: [string, unknown][]): {
  regularFields: [string, unknown][];
  claimRows: ClaimRow[];
} {
  const claimMap = new Map<number, ClaimRow>();
  const regularFields: [string, unknown][] = [];

  for (const [key, value] of entries) {
    const match = key.match(CLAIM_FIELD_PATTERN);
    if (match) {
      const num = Number.parseInt(match[1]);
      const prop = match[2] as 'Date' | 'Nature' | 'Responsibility';
      if (!claimMap.has(num)) {
        claimMap.set(num, { number: num, date: null, nature: null, responsibility: null });
      }
      const row = claimMap.get(num)!;
      if (prop === 'Date') row.date = { key, value };
      else if (prop === 'Nature') row.nature = { key, value };
      else row.responsibility = { key, value };
    } else {
      regularFields.push([key, value]);
    }
  }

  return {
    regularFields,
    claimRows: [...claimMap.values()].sort((a, b) => a.number - b.number),
  };
}

export function ExtractionFieldsDisplay({
  extraction,
  isLoading,
  extractionEnabled,
  onFeedback,
  isFeedbackPending,
  reportedFields,
  isDocumentReported,
}: ExtractionFieldsDisplayProps) {
  const { t } = useTranslation();
  const [editingField, setEditingField] = useState<string | null>(null);

  if (!extractionEnabled) return null;

  if (isLoading) {
    return (
      <Group align="stretch" className="py-1">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-28" />
      </Group>
    );
  }

  if (!extraction || extraction.status === 'SKIPPED') {
    return null;
  }

  if (extraction.status === 'PENDING') {
    return (
      <Text inline size="xs" tone="muted">
        {t('documents.extraction.status.pending')}
      </Text>
    );
  }

  if (extraction.status === 'FAILED') {
    return (
      <Text inline size="xs" className="text-red-600">
        {t('documents.extraction.status.failed')}
      </Text>
    );
  }

  const fields = extraction.fields as Record<string, unknown> | undefined;
  if (!fields || Object.keys(fields).length === 0) {
    return (
      <Text inline size="xs" tone="muted">
        {t('documents.extraction.noFields')}
      </Text>
    );
  }

  const fieldEntries = Object.entries(fields);

  if (editingField) {
    return (
      <div className="max-w-md py-1">
        <ExtractionFeedbackForm
          feedbackType="FIELD_CORRECTION"
          fieldKey={editingField}
          originalValue={
            fields[editingField] === null || fields[editingField] === undefined
              ? undefined
              : String(fields[editingField])
          }
          onSubmit={async feedback => {
            try {
              await onFeedback?.(feedback);
              setEditingField(null);
            } catch {
              // keep form open for retry
            }
          }}
          onCancel={() => setEditingField(null)}
          isPending={isFeedbackPending}
        />
      </div>
    );
  }

  const renderFieldValue = (fieldKey: string, value: unknown) => {
    const rawValue = value === null || value === undefined ? '-' : String(value);
    const reportedInfo = reportedFields?.get(fieldKey);
    const isFieldReported = !!reportedInfo;

    return (
      <>
        <Text inline weight="medium" className="min-w-0 break-words">
          {rawValue}
        </Text>
        {onFeedback && !isDocumentReported && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={
                    isFieldReported
                      ? t('documents.extraction.feedback.fieldReported')
                      : t('documents.extraction.feedback.reportField')
                  }
                  className={cn(
                    'h-9 w-9 flex-shrink-0 transition-opacity',
                    isFieldReported
                      ? 'opacity-100 text-amber-500 cursor-default pointer-events-none'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                  disabled={isFieldReported || isFeedbackPending}
                  onClick={() =>
                    !isFieldReported && !isFeedbackPending && setEditingField(fieldKey)
                  }
                >
                  <FlagIcon className="w-3.5 h-3.5" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {isFieldReported ? (
                <Stack className="gap-0.5">
                  <p>{t('documents.extraction.feedback.fieldReported')}</p>
                  {reportedInfo?.correctedValue && (
                    <Text tone="muted">
                      {t('documentReview.expectedValue')}: {reportedInfo.correctedValue}
                    </Text>
                  )}
                  {reportedInfo?.comment && <Text tone="muted">{reportedInfo.comment}</Text>}
                </Stack>
              ) : (
                t('documents.extraction.feedback.reportField')
              )}
            </TooltipContent>
          </Tooltip>
        )}
      </>
    );
  };

  const { regularFields, claimRows } = groupFields(fieldEntries);

  return (
    <div>
      {regularFields.length > 0 && (
        <dl className="divide-y divide-border/60">
          {regularFields.map(([key, value]) => (
            <div
              key={key}
              className="group grid grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] items-start gap-x-4 py-3 first:pt-0 last:pb-0"
            >
              <dt className="pt-2 text-sm font-normal leading-5 text-muted-foreground">
                {t(`documents.extraction.fields.${key}`, key)}
              </dt>
              <dd className="flex min-h-9 min-w-0 items-center justify-between gap-2 break-words text-base leading-6 text-foreground">
                {renderFieldValue(key, value)}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {claimRows.length > 0 && (
        <div className={cn(regularFields.length > 0 && 'mt-1.5')}>
          <Text inline size="xs" tone="muted" weight="medium">
            {t('documents.extraction.claims.title')}
          </Text>
          <table className="mt-0.5 text-xs">
            <thead>
              <tr className="text-muted-foreground border-b border-border/50">
                <th className="pr-4 py-0.5 text-left font-medium">#</th>
                <th className="pr-4 py-0.5 text-left font-medium">
                  {t('documents.extraction.claims.date')}
                </th>
                <th className="pr-4 py-0.5 text-left font-medium">
                  {t('documents.extraction.claims.nature')}
                </th>
                <th className="pr-4 py-0.5 text-left font-medium">
                  {t('documents.extraction.claims.responsibility')}
                </th>
              </tr>
            </thead>
            <tbody>
              {claimRows.map(row => (
                <tr key={row.number}>
                  <td className="pr-4 py-0.5 text-muted-foreground">{row.number}</td>
                  <td className="group pr-4 py-0.5">
                    {row.date ? renderFieldValue(row.date.key, row.date.value) : '-'}
                  </td>
                  <td className="group pr-4 py-0.5">
                    {row.nature ? renderFieldValue(row.nature.key, row.nature.value) : '-'}
                  </td>
                  <td className="group pr-4 py-0.5">
                    {row.responsibility
                      ? renderFieldValue(row.responsibility.key, row.responsibility.value)
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
